import React, { useState } from 'react';
import { isOrangePiBoard } from '@/utils/orangePi';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { useReflashingStore } from '@/store/useReflashingStore';
import { rebindClaim } from '@/api/vendo';
import type { VendoDevice } from '@/pages/Admin/Vendo';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Rebind Router — the Lite twin of MikroTik Reflash. Reattaches a factory-reset,
 * reflashed or replacement Lite box to THIS device (keeping its license, time,
 * sub-vendos and portal) by entering the fresh claim code the box shows. The
 * server hands the box this device's identity; the box reconnects AS this
 * device, and its serial reconciles the license on the next heartbeat.
 *
 * Covers Ruijie AND OrangePi — the wording below switches on the board because
 * the trigger differs: a Ruijie gets factory-reset, an OrangePi gets its SD card
 * rewritten. The OrangePi case is the more common one, since a reflash there
 * otherwise forces a delete-and-re-register and loses everything.
 */

type Phase = 'intro' | 'waiting' | 'done' | 'error';

const RebindRouter: React.FC<{ data: { device: VendoDevice } }> = ({ data }) => {
   const device = data.device;
   const { handleClose } = useUIStore();
   const refetch = useVendoDevicesStore((s) => s.fetch);
   const markReflashing = useReflashingStore((s) => s.mark);
   const [phase, setPhase] = useState<Phase>('intro');
   const [code, setCode] = useState('');
   const [errMsg, setErrMsg] = useState('');
   // Board decides the wording only — the flow itself is identical. hwBoard can
   // arrive top-level or under router depending on which object opened this
   // dialog, so check both (same defensive read as DeviceSettings).
   const isOrangePi = isOrangePiBoard(
      (device as { hwBoard?: string }).hwBoard ||
         (device as { router?: { hwBoard?: string } }).router?.hwBoard
   );

   const submit = async () => {
      const c = code.trim();
      if (!c) {
         setErrMsg('Enter the claim code shown on the router.');
         return;
      }
      setErrMsg('');
      setPhase('waiting');
      try {
         await rebindClaim(device.id, c);
      } catch (err) {
         setErrMsg(apiErr(err, 'Rebind failed — check the code and try again.'));
         setPhase('error');
         return;
      }
      // The box picks up this device's identity on its next unclaimed heartbeat
      // (~10-30s) and reconnects AS this device. Mark it "setting up" so the card
      // shows the banner, poll a short window, then finish — it keeps reconnecting
      // server-side regardless of this poll.
      markReflashing(device._id);
      for (let i = 0; i < 12; i++) {
         await sleep(5000);
         try {
            await refetch();
         } catch {
            /* transient — keep waiting */
         }
      }
      setPhase('done');
   };

   return (
      <Wrap>
         {phase === 'intro' && (
            <>
               <Lead>
                  {isOrangePi ? (
                     <>
                        Reflashed or replacing <b>{device.name}</b>? Rebind puts the
                        box back online <b>as this same vendo</b> — your license,
                        remaining time, sub-vendos, portal and coin pin settings all
                        carry over. You are not charged again, and you do not need to
                        delete and register it again.
                     </>
                  ) : (
                     <>
                        Hard-reset or replacing <b>{device.name}</b>? Rebind puts the
                        reset (or new) Ruijie back online <b>as this device</b> — your
                        license, remaining time, sub-vendos and portal all carry over.
                        You are not charged again.
                     </>
                  )}
               </Lead>
               <Note>
                  <b>1.</b>{' '}
                  {isOrangePi
                     ? 'Write the SmartFi image to the SD card and power the box on (or power on the replacement).'
                     : 'Factory-reset the Ruijie (or power on the new one).'}
                  <br />
                  <b>2.</b> It shows a <b>claim code</b> at <code>10.0.0.1</code>.
                  <br />
                  <b>3.</b> Enter that code below.
               </Note>
               <FieldLabel>Claim code from the router</FieldLabel>
               <CodeInput
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABC-123"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && submit()}
               />
               {errMsg && <ErrText>{errMsg}</ErrText>}
               <PrimaryBtn onClick={submit}>Rebind Router</PrimaryBtn>
            </>
         )}

         {phase === 'waiting' && (
            <Center>
               <Spinner />
               <Muted>
                  Reattaching {device.name}… the router will reconnect in ~30s.
               </Muted>
            </Center>
         )}

         {phase === 'done' && (
            <Center>
               <BigCheck>✓</BigCheck>
               <Lead style={{ textAlign: 'center' }}>
                  <b>{device.name}</b> is rebinding. The router reconnects shortly
                  with your license and settings unchanged.
               </Lead>
               <PrimaryBtn onClick={handleClose}>Done</PrimaryBtn>
            </Center>
         )}

         {phase === 'error' && (
            <Center>
               <ErrText>{errMsg}</ErrText>
               <PrimaryBtn onClick={() => setPhase('intro')}>Try again</PrimaryBtn>
            </Center>
         )}
      </Wrap>
   );
};

export default RebindRouter;

function apiErr(err: unknown, fallback: string): string {
   const data = (err as { response?: { data?: { msg?: string; error?: string } } })
      ?.response?.data;
   return data?.msg || data?.error || fallback;
}

const spin = keyframes`to { transform: rotate(360deg); }`;

const Wrap = styled.div`
   padding: 4px 2px;
   max-width: 460px;
`;
const Lead = styled.p`
   margin: 0 0 14px;
   font-size: 14px;
   line-height: 1.55;
   color: ${colors.DARK};
`;
const Note = styled.div`
   margin: 0 0 18px;
   padding: 10px 12px;
   border-radius: 8px;
   background: ${colors.PRIMARY}12;
   border: 1px solid ${colors.PRIMARY}33;
   font-size: 12.5px;
   line-height: 1.7;
   color: ${colors.DARK};
   code {
      background: ${colors.DARK};
      color: #e6edf3;
      padding: 1px 5px;
      border-radius: 4px;
      font-size: 11.5px;
   }
`;
const FieldLabel = styled.label`
   display: block;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY};
   margin-bottom: 5px;
`;
const CodeInput = styled.input`
   width: 100%;
   padding: 11px 12px;
   border: 1px solid #d8d8d8;
   border-radius: 8px;
   font-size: 18px;
   font-weight: 700;
   letter-spacing: 0.12em;
   text-align: center;
   text-transform: uppercase;
   margin-bottom: 12px;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;
const ErrText = styled.p`
   margin: 0 0 12px;
   font-size: 12.5px;
   color: ${colors.DANGER};
   text-align: center;
`;
const PrimaryBtn = styled.button`
   width: 100%;
   padding: 11px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-size: 14px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      opacity: 0.92;
   }
`;
const Center = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 14px;
   padding: 14px 2px;
`;
const Muted = styled.p`
   margin: 0;
   font-size: 12.5px;
   color: ${colors.GRAY};
   text-align: center;
`;
const Spinner = styled.div`
   width: 34px;
   height: 34px;
   border: 3px solid ${colors.PRIMARY}30;
   border-top-color: ${colors.PRIMARY};
   border-radius: 50%;
   animation: ${spin} 0.8s linear infinite;
`;
const BigCheck = styled.div`
   width: 56px;
   height: 56px;
   border-radius: 50%;
   background: ${colors.SUCCESS};
   color: #fff;
   font-size: 30px;
   display: flex;
   align-items: center;
   justify-content: center;
`;
